import React from "react";
import { GridLayout } from "components/grid";
import { DataDisplayRecord } from "components/data-display";
import { DateToLocal } from "components/dates";
import { GreyFleetCheckVehicleOverview } from "pages/licence-checking/grey-fleet/check/overview";
import { ShowHide } from "components/layout";
import { StatusBadge } from "components/status";
import PropTypes from "prop-types";

const defaultArray = [];

export function GreyFleetCheckVehicle({
    isLoading,
    checkData,
    evaluateStatus,
    refreshCheck,
    licenceCheckingOptions,
    policy,
    coverTypes = defaultArray,
}) {
    function getToolTipMessage(ruleKey) {
        const policyRule = policy.find(
            (rule) => rule.policyRuleKey === ruleKey
        );

        if (!policyRule) {
            return "No additional information available.";
        }

        const options = licenceCheckingOptions.fleetCheckPolicyRules.find(
            (option) => option.key === ruleKey
        );

        const values =
            policyRule.values.length > 1
                ? policyRule.values.join(", ")
                : policyRule.values[0];

        return `${options.name} - ${values} ${options.suffix}`;
    }

    function getCylinderCapacityTooltipMessage() {
        if (checkData?.failingPolicyRules?.includes("MINCC")) {
            return (
                <StatusBadge
                    intent="danger"
                    text={checkData?.asset?.engineCapacity}
                    iconName="error"
                    showTooltip
                    tooltipContent={getToolTipMessage("MINCC")}
                />
            );
        } else if (checkData?.failingPolicyRules?.includes("MAXCC")) {
            return (
                <StatusBadge
                    intent="danger"
                    text={checkData?.asset?.engineCapacity}
                    iconName="error"
                    showTooltip
                    tooltipContent={getToolTipMessage("MAXCC")}
                />
            );
        } else {
            return checkData?.asset?.engineCapacity || "No Record";
        }
    }

    return (
        <div>
            <GreyFleetCheckVehicleOverview
                isLoading={isLoading}
                checkData={checkData}
                evaluateStatus={evaluateStatus}
                refreshCheck={refreshCheck}
                licenceCheckingOptions={licenceCheckingOptions}
                coverTypes={coverTypes}
            />

            <ShowHide
                evaluator={checkData.asset.result !== null}
                show={
                    <div className="spacer-bottom">
                        <h2>Vehicle Details</h2>
                        <GridLayout numberOfColumns={4} gap={30}>
                            <DataDisplayRecord
                                header="Vehicle make"
                                data={checkData?.asset?.make || "No Record"}
                                loading={isLoading}
                            />
                            <DataDisplayRecord
                                header="Date of First Registration"
                                data={
                                    checkData?.failingPolicyRules?.includes(
                                        "VEHICLEAGE"
                                    ) ? (
                                        <StatusBadge
                                            intent="danger"
                                            text={
                                                <DateToLocal format="DD/MM/YYYY">
                                                    {checkData?.asset
                                                        ?.registrationDate ??
                                                        ""}
                                                </DateToLocal>
                                            }
                                            iconName="error"
                                            showTooltip
                                            tooltipContent={getToolTipMessage(
                                                "VEHICLEAGE"
                                            )}
                                        />
                                    ) : (
                                        <DateToLocal format="DD/MM/YYYY">
                                            {checkData?.asset
                                                ?.registrationDate ?? ""}
                                        </DateToLocal>
                                    )
                                }
                                loading={isLoading}
                            />
                            <DataDisplayRecord
                                header="Year of Manufacture"
                                data={
                                    checkData?.failingPolicyRules.includes(
                                        "VEHICLEAGE"
                                    ) ? (
                                        <StatusBadge
                                            intent="danger"
                                            text={
                                                checkData?.asset
                                                    ?.yearOfManufacture ||
                                                "No Record"
                                            }
                                            iconName="error"
                                            showTooltip
                                            tooltipContent={getToolTipMessage(
                                                "VEHICLEAGE"
                                            )}
                                        />
                                    ) : (
                                        checkData?.asset?.yearOfManufacture ||
                                        "No Record"
                                    )
                                }
                                loading={isLoading}
                            />
                            <DataDisplayRecord
                                header="Cylinder Capacity"
                                data={getCylinderCapacityTooltipMessage()}
                                loading={isLoading}
                            />
                            <DataDisplayRecord
                                header="CO2 Emissions"
                                data={
                                    checkData?.failingPolicyRules?.includes(
                                        "MAXCO2"
                                    ) ? (
                                        <StatusBadge
                                            intent="danger"
                                            text={
                                                checkData?.asset?.co2Emissions
                                            }
                                            iconName="error"
                                            showTooltip
                                            tooltipContent={getToolTipMessage(
                                                "MAXCO2"
                                            )}
                                        />
                                    ) : (
                                        checkData?.asset?.co2Emissions ||
                                        "No Record"
                                    )
                                }
                                loading={isLoading}
                            />
                            <DataDisplayRecord
                                header="Fuel Type"
                                data={
                                    checkData?.failingPolicyRules?.includes(
                                        "FUELTYPE"
                                    ) ? (
                                        <StatusBadge
                                            intent="danger"
                                            text={checkData?.asset?.fuelType}
                                            iconName="error"
                                            showTooltip
                                            tooltipContent={getToolTipMessage(
                                                "FUELTYPE"
                                            )}
                                        />
                                    ) : (
                                        checkData?.asset?.fuelType ||
                                        "No Record"
                                    )
                                }
                                loading={isLoading}
                            />
                            <DataDisplayRecord
                                header="Euro Status"
                                data={
                                    checkData?.failingPolicyRules?.includes(
                                        "EUROSTATUS"
                                    ) ? (
                                        <StatusBadge
                                            intent="danger"
                                            text={checkData?.asset?.euroStatus}
                                            iconName="error"
                                            showTooltip
                                            tooltipContent={getToolTipMessage(
                                                "EUROSTATUS"
                                            )}
                                        />
                                    ) : (
                                        checkData?.asset?.euroStatus ||
                                        "No Record"
                                    )
                                }
                                loading={isLoading}
                            />
                            <DataDisplayRecord
                                header="Odometer Reading"
                                data={
                                    checkData?.failingPolicyRules?.includes(
                                        "MAXODO"
                                    ) ? (
                                        <StatusBadge
                                            intent="danger"
                                            text={
                                                checkData?.asset
                                                    ?.odometerReading
                                            }
                                            iconName="error"
                                            showTooltip
                                            tooltipContent={getToolTipMessage(
                                                "MAXODO"
                                            )}
                                        />
                                    ) : (
                                        checkData?.asset?.odometerReading ||
                                        "No Record"
                                    )
                                }
                                loading={isLoading}
                            />
                        </GridLayout>
                    </div>
                }
            />

            <div className="spacer-bottom">
                <h2>Tax Details</h2>
                <GridLayout numberOfColumns={4} gap={30}>
                    <DataDisplayRecord
                        header="Tax Status"
                        data={evaluateStatus(
                            checkData?.tax?.status,
                            checkData?.tax?.statusKey,
                            checkData?.tax?.result,
                            "TAXFAILURE"
                        )}
                        loading={isLoading}
                    />
                    <ShowHide
                        evaluator={checkData?.tax?.result !== null}
                        show={
                            <DataDisplayRecord
                                header="Tax Due Date"
                                data={
                                    <DateToLocal format="DD/MM/YYYY">
                                        {checkData?.tax?.dueDate ?? ""}
                                    </DateToLocal>
                                }
                                loading={isLoading}
                            />
                        }
                    />
                </GridLayout>
            </div>

            <h2>MOT Details</h2>
            <GridLayout numberOfColumns={4} gap={30}>
                <DataDisplayRecord
                    header="MOT Status"
                    data={evaluateStatus(
                        checkData?.mot?.status,
                        checkData?.mot?.statusKey,
                        checkData?.mot?.result,
                        "MOTFAILURE"
                    )}
                    loading={isLoading}
                />
                <ShowHide
                    evaluator={checkData?.mot?.result !== null}
                    show={
                        <DataDisplayRecord
                            header="MOT Due Date"
                            data={
                                <DateToLocal format="DD/MM/YYYY">
                                    {checkData?.mot?.dueDate ?? ""}
                                </DateToLocal>
                            }
                            loading={isLoading}
                        />
                    }
                />
            </GridLayout>
        </div>
    );
}

GreyFleetCheckVehicle.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    checkData: PropTypes.object.isRequired,
    evaluateStatus: PropTypes.func.isRequired,
    refreshCheck: PropTypes.func.isRequired,
    licenceCheckingOptions: PropTypes.object.isRequired,
    policy: PropTypes.object.isRequired,
    coverTypes: PropTypes.array,
};
